<script setup>
import { ref, computed, onMounted, onUnmounted, watch, inject } from 'vue';
import { useMenuStore } from '@/js/store/menuStore';
import { throttle } from 'throttle-debounce';
import Directions from './Directions.vue';
import ButtonWithCounter from '@/js/vue-components/ButtonWithCounter.vue';
import ThirdLevelMenu from './ThirdLevelMenu.vue';
import SecondLevelMenu from './SecondLevelMenu.vue';
import HeaderIcon from './HeaderIcon.vue';

const props = defineProps({
    menu: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    compare: {
        type: Array,
        default: null,
    },
    favorite: {
        type: Array,
        default: null,
    },
});

const scrollbarPlugin = inject('scrollbarPlugin');
const showDirections = ref(false);
const menuStore = useMenuStore();
const isMobileMenuOpen = ref(false);
const activeThirdMenuCode = ref(null);
const activeThirdMenuTitle = ref(null);
const headerHeight = ref(0);
const secondMenu = ref(null);
const directions = ref(null);
const headerRef = ref(null);
const resizeObserverRef = ref(null);

// Computed
const menuItems = computed(() => menuStore.menuItems);
const activeSecondLevelMenu = computed(() => menuStore.activeSecondMenuItems);
const activeThirdLevelMenu = computed(() =>
    activeThirdMenuCode.value
        ? menuStore.thirdMenuItems(activeThirdMenuCode.value)
        : []
);

// Watch
watch(
    () => props.menu,
    (newMenu) => {
        if (newMenu) {
            menuStore.updateMenu(newMenu);
        }
    },
    { immediate: true }
);

watch(
    () => activeThirdMenuCode.value,
    (newValue) => {
        if (newValue) {
            scrollbarPlugin.applySettings(
                activeThirdLevelMenu.value.length > 0
            );
        } else {
            scrollbarPlugin.applySettings(false);
            if (menuStore.activeSecondMenu.selected === 'Y') {
                const activeItem = menuStore.activeSecondMenu.sections.find(
                    (item) => item.selected === 'Y'
                );
                if (activeItem) {
                    menuStore.updateSecondActiveLink(activeItem.code);
                }
            }
        }
    }
);

watch(
    () => showDirections.value,
    (newValue) => scrollbarPlugin.applySettings(newValue, 'blur-all-directions')
);

watch(
    () => isMobileMenuOpen.value,
    (newValue) => {
        document.body.style.overflow = newValue ? 'hidden' : '';
    }
);

// Methods
const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const setActiveThirdMenu = (code, name) => {
    activeThirdMenuCode.value =
        code === activeThirdMenuCode.value ? null : code;
    menuStore.updateSecondActiveLink(code);
    activeThirdMenuTitle.value = name;
};

// Отслеживание скролла
const throttledHandleScroll = throttle(300, () => {
    if (menuStore.activeSecondMenu.selected === 'Y') {
        activeThirdMenuCode.value = null;

        if (Object.hasOwn(menuStore.activeSecondMenu, 'sections')) {
            const activeItem = menuStore.activeSecondMenu.sections.find(
                (item) => item.selected === 'Y'
            );
            if (activeItem) {
                menuStore.updateSecondActiveLink(activeItem.code);
            }
        }
        return;
    }

    if (activeSecondLevelMenu.value.length) {
        menuStore.updateSecondMenu(null);
        activeThirdMenuCode.value = null;
    }
});

// Сlick outside
const handleClickOutside = (event) => {
    if (!secondMenu.value || secondMenu.value.contains(event.target)) return;

    if (menuStore.activeSecondMenu.selected === 'Y') {
        activeThirdMenuCode.value = null;
    } else {
        menuStore.updateSecondMenu(null);
        activeThirdMenuCode.value = null;
    }

    if (!directions.value.contains(event.target)) {
        showDirections.value = false;
    }
};

onMounted(() => {
    // Компенсация высоты хедера
    headerHeight.value = document.querySelector('.header')?.offsetHeight || 0;

    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', throttledHandleScroll);

    const resizeObserver = new ResizeObserver((entries) => {
        if (entries[0]) {
            const entry = entries[0];
            const styles = window.getComputedStyle(entry.target);
            headerHeight.value =
                entry.target.offsetHeight + parseFloat(styles.marginBottom);
        }
    });

    const header = document.querySelector('.header');
    if (header) {
        resizeObserver.observe(header);
        headerRef.value = header;
        resizeObserverRef.value = resizeObserver;
    }
});
</script>

<template>
    <div
        class="_nav-all-directions-popup"
        :class="{ active: showDirections }"
        ref="directions"
    >
        <transition name="direction">
            <directions
                :directions="menuStore.menu['all-directions']?.sections"
                v-if="showDirections"
                @closePanel="showDirections = false"
            ></directions>
        </transition>
    </div>

    <div class="header header--scrolled" ref="headerRef">
        <div ref="secondMenu">
            <!-- Навигация -->
            <div class="header__container">
                <nav class="header-menu__nav--first">
                    <div class="header-menu__nav">
                        <div
                            class="header-menu__directions-button"
                            @click="showDirections = !showDirections"
                        >
                            <header-icon name="directions" />
                        </div>

                        <a class="header-menu__nav__logo-wrap" href="/">
                            <picture>
                                <source
                                    srcset="
                                        /static/dist/land-v2/img/logo-DOM-RF-land-mobile.svg
                                    "
                                    media="(max-width: 767px)"
                                />
                                <img
                                    src="/static/dist/land-v2/img/logo-DOM-RF-land.svg"
                                    alt="ДОМ РФ Земля"
                                    loading="lazy"
                                />
                            </picture>
                        </a>

                        <!-- Элементы меню -->
                        <a
                            v-for="item in menuItems"
                            :key="item.code"
                            class="header-menu__link header-menu__link--desktop"
                            :href="item?.url"
                            :target="item._blank === 'Y' ? '_blank' : '_self'"
                            :class="{
                                'header-menu__link--active':
                                    item.code === menuStore.activeLinkFirstMenu,
                            }"
                        >
                            {{ item.name }}
                        </a>
                    </div>

                    <div class="header-menu__nav__info">
                        <div class="header-menu__nav__info-compare">
                            <button-with-counter
                                type="compare"
                                :class="{
                                    active:
                                        menuStore.activePath ===
                                        '/auctions/compare/',
                                }"
                                :data="compare"
                            >
                                <header-icon name="compare" />
                            </button-with-counter>
                        </div>

                        <div class="header-menu__nav__info-favorite">
                            <button-with-counter
                                type="favorite"
                                :class="{
                                    active:
                                        menuStore.activePath ===
                                        '/auctions/favorites/',
                                }"
                                :data="favorite"
                            >
                                <header-icon name="favorite" />
                            </button-with-counter>
                        </div>

                        <div class="header-menu__nav__info-contacts">
                            <a
                                class="header-menu__nav__info-phone"
                                href="tel:88007700001"
                            >
                                <header-icon name="phone" />
                                8 (800) 770-00-01
                            </a>
                        </div>

                        <!-- Mobile -->
                        <button
                            class="header-menu__burger"
                            @click="toggleMobileMenu"
                            :aria-expanded="isMobileMenuOpen"
                            aria-controls="mobile-menu"
                            :class="{
                                'header-menu__burger--active': isMobileMenuOpen,
                            }"
                        >
                            <header-icon
                                :name="isMobileMenuOpen ? 'close' : 'menu'"
                            />
                        </button>
                    </div>
                </nav>
            </div>

            <!-- Второй уровень навигации -->
            <second-level-menu
                v-if="activeSecondLevelMenu.length"
                :active-path="menuStore.activePath"
                :menu-items="activeSecondLevelMenu"
                :active-menu-code="menuStore.activeLinkSecondMenu"
                :active-third-menu="activeThirdLevelMenu.length > 0"
                @select-item="setActiveThirdMenu"
            />

            <!-- Третий уровень навигации -->
            <third-level-menu
                v-if="activeThirdLevelMenu.length"
                :menu-items="activeThirdLevelMenu"
                :active-path="menuStore.activePath"
                @close="activeThirdMenuCode = null"
            />
        </div>

        <!-- Мобильное меню -->
        <div
            v-if="isMobileMenuOpen"
            class="header-menu__mobile-menu"
            id="mobile-menu"
        >
            <nav class="header-menu__mobile-nav">
                <!-- Навигация -->
                <ul class="header-menu__mobile-nav-first">
                    <li v-for="item in menuItems" :key="item.code">
                        <a
                            class="header-menu__mobile-nav-link"
                            :href="item?.url"
                            :target="item._blank === 'Y' ? '_blank' : '_self'"
                            :class="{
                                'header-menu__mobile-nav-link--active':
                                    item.code === menuStore.activeLinkFirstMenu,
                            }"
                        >
                            {{ item.name }}
                        </a>
                    </li>
                </ul>

                <!-- Моб. версия - второй уровень -->
                <second-level-menu
                    v-if="activeSecondLevelMenu.length"
                    :menu-items="activeSecondLevelMenu"
                    :active-menu-code="menuStore.activeLinkSecondMenu"
                    :active-third-menu="activeThirdLevelMenu.length > 0"
                    is-mobile
                    @select-item="setActiveThirdMenu"
                />

                <!-- Моб. версия - третий уровень -->
                <third-level-menu
                    v-if="activeThirdLevelMenu.length"
                    :title="activeThirdMenuTitle"
                    :menu-items="activeThirdLevelMenu"
                    :active-path="menuStore.activePath"
                    is-mobile
                    @close="activeThirdMenuCode = null"
                />
            </nav>
        </div>
    </div>

    <!-- Компенсация высоты хедера -->
    <div
        class="header__spacer"
        :style="{ paddingTop: `${headerHeight}px` }"
        aria-hidden="true"
    />
</template>
